// useHospitalNeighbours — every hospital within 300 straight-line miles of a
// hospital, from scenario_coverage_manifest.json (coordinates + third-party
// quality signals for ~6.6k hospitals), plus a price-grade lookup for a set
// of hospital ids from hospital_ranking_summary.
//
// Shared by the City view tab and the Scenario Pricing tab's distance tool
// (react-query dedupes, so switching tabs costs nothing). Distances are
// haversine, NOT driving distance; bands are cumulative ("within N miles"),
// the same semantics as the precomputed local_* columns.
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { getAllHospitals, haversineDistance, loadHospitalManifest, type HospitalInfo } from "@/lib/scenarioCoverageStore";

export const NEIGHBOUR_RADII = [30, 60, 120, 300] as const;
export type NeighbourRadius = (typeof NEIGHBOUR_RADII)[number];
export const NEIGHBOUR_MAX_MILES = 300;

export interface Neighbour extends HospitalInfo {
  id: string;
  miles: number;
}

export interface NeighbourGrade {
  hospital_id: string;
  median_gross_markup_percentage: number | null;
  avg_gross_markup_percentage: number | null;
  overall_rank_gross: number | null;
  /** Set by Stage 9.1 of the ranking rebuild for hospitals whose prices are not
   *  comparably gradeable: psychiatric / long-term / rehab facilities, anything
   *  under 50 non-REV procedures, and carried-over data-quality audit flags.
   *  The hospital is real and stays on the map — it just must not show a letter
   *  grade, because no defensible national baseline was computed for it.
   *  Duplicate RECORDS are a different problem, removed upstream in
   *  scenarioCoverageStore via hospital_suppressions.json. */
  exclude_from_rankings: boolean | null;
}

const GRADE_LOOKUP_CHUNK = 100;

export function useHospitalNeighbours(hospitalId: string | undefined, lat: number | null, lng: number | null) {
  const q = useQuery({
    queryKey: ["hospital-neighbours", hospitalId, lat, lng],
    enabled: !!hospitalId && lat != null && lng != null,
    staleTime: Infinity,
    queryFn: async () => {
      await loadHospitalManifest();
      const all = getAllHospitals();
      const out: Neighbour[] = [];
      for (const [id, h] of Object.entries(all)) {
        if (id === hospitalId || h.la == null || h.lo == null) continue;
        const miles = haversineDistance(lat!, lng!, h.la, h.lo);
        if (miles <= NEIGHBOUR_MAX_MILES) out.push({ id, miles, ...h });
      }
      out.sort((a, b) => a.miles - b.miles);
      return out;
    },
  });
  const counts = useMemo(() => {
    const c: Record<NeighbourRadius, number> = { 30: 0, 60: 0, 120: 0, 300: 0 };
    for (const n of q.data || []) for (const r of NEIGHBOUR_RADII) if (n.miles <= r) c[r]++;
    return c;
  }, [q.data]);
  return { neighbours: q.data ?? null, counts, isLoading: q.isLoading };
}

/** Median-baseline markup + national rank for a set of hospitals (chunked). */
export function useNeighbourGrades(ids: string[]) {
  const key = ids.join("|");
  const q = useQuery({
    queryKey: ["neighbour-grades", key],
    enabled: ids.length > 0,
    staleTime: 1000 * 60 * 60,
    queryFn: async () => {
      const map = new Map<string, NeighbourGrade>();
      for (let i = 0; i < ids.length; i += GRADE_LOOKUP_CHUNK) {
        const chunk = ids.slice(i, i + GRADE_LOOKUP_CHUNK);
        const { data, error } = await supabase
          .from("hospital_ranking_summary")
          .select("hospital_id, median_gross_markup_percentage, avg_gross_markup_percentage, overall_rank_gross, exclude_from_rankings")
          .in("hospital_id", chunk);
        if (error) throw error;
        for (const r of (data || []) as NeighbourGrade[]) map.set(r.hospital_id, r);
      }
      return map;
    },
  });
  const grades = q.data;
  const markupOf = (id: string): number | null => {
    const g = grades?.get(id);
    if (!g || g.exclude_from_rankings) return null;
    const m = g.median_gross_markup_percentage ?? g.avg_gross_markup_percentage;
    return m != null && Number.isFinite(Number(m)) ? Number(m) : null;
  };
  const rankOf = (id: string): number | null => {
    const g = grades?.get(id);
    if (!g || g.exclude_from_rankings) return null;
    return g.overall_rank_gross != null ? Number(g.overall_rank_gross) : null;
  };
  return { grades, markupOf, rankOf, isLoading: q.isLoading };
}
